import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { CustomerStatus, InvoiceStatus, TeamMemberStatus } from '@/types'

import {
    Ban,
    CircleCheck,
    CircleX,
    Clock,
    Hourglass,
    MailQuestion,
    RotateCcw,
    TriangleAlert
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { CUSTOMER_STATUS_LABELS, INVOICE_STATUS_LABELS } from '@/constants/dashboard'

export type StatusTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral'

interface StatusBadgeProps {
    tone: StatusTone
    icon: LucideIcon
    children: ReactNode
    className?: string
}

const TONES: Record<StatusTone, string> = {
    success: 'bg-success/12 text-success',
    warning: 'bg-warning/14 text-warning',
    danger: 'bg-destructive/10 text-destructive',
    info: 'bg-info/12 text-info',
    neutral: 'bg-muted text-muted-foreground'
}

// Status colors are reserved for state and always come with an icon and a label
export const StatusBadge: FC<StatusBadgeProps> = ({ tone, icon: Icon, children, className }): ReactNode => {
    return (
        <span className={cn('inline-flex h-6 items-center gap-1 rounded-full px-2 text-xs font-medium whitespace-nowrap', TONES[tone], className)}>
            <Icon className='size-3.5 shrink-0' aria-hidden='true' />
            {children}
        </span>
    )
}

const CUSTOMER_STATUS: Record<CustomerStatus, { tone: StatusTone, icon: LucideIcon }> = {
    active: { tone: 'success', icon: CircleCheck },
    trial: { tone: 'info', icon: Hourglass },
    'at-risk': { tone: 'warning', icon: TriangleAlert },
    canceled: { tone: 'neutral', icon: Ban }
}

const INVOICE_STATUS: Record<InvoiceStatus, { tone: StatusTone, icon: LucideIcon }> = {
    paid: { tone: 'success', icon: CircleCheck },
    pending: { tone: 'warning', icon: Clock },
    failed: { tone: 'danger', icon: CircleX },
    refunded: { tone: 'neutral', icon: RotateCcw }
}

interface CustomerStatusBadgeProps {
    status: CustomerStatus
    className?: string
}

interface InvoiceStatusBadgeProps {
    status: InvoiceStatus
    className?: string
}

interface MemberStatusBadgeProps {
    status: TeamMemberStatus
    className?: string
}

export const CustomerStatusBadge: FC<CustomerStatusBadgeProps> = ({ status, className }): ReactNode => {
    const config = CUSTOMER_STATUS[status]

    return (
        <StatusBadge tone={config.tone} icon={config.icon} className={className}>
            {CUSTOMER_STATUS_LABELS[status]}
        </StatusBadge>
    )
}

export const InvoiceStatusBadge: FC<InvoiceStatusBadgeProps> = ({ status, className }): ReactNode => {
    const config = INVOICE_STATUS[status]

    return (
        <StatusBadge tone={config.tone} icon={config.icon} className={className}>
            {INVOICE_STATUS_LABELS[status]}
        </StatusBadge>
    )
}

export const MemberStatusBadge: FC<MemberStatusBadgeProps> = ({ status, className }): ReactNode => {
    return status === 'active'
        ? (
            <StatusBadge tone='success' icon={CircleCheck} className={className}>
                Aktif
            </StatusBadge>
        )
        : (
            <StatusBadge tone='info' icon={MailQuestion} className={className}>
                Menunggu
            </StatusBadge>
        )
}
